import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Api } from "@/api";
import {
  setDeliveryListAdditional,
  setParam,
} from "@/redux/action-creaters/cart";
import { DeliveryTypes, UserFields } from "@/types/cart";
import { cacheFunctionData } from "@/utils";
import Button from "~/components/common/button";
import { showPopup } from "~/redux/action-creaters/popup";
import { popupTypes } from "~/redux/reducers/popupReducer";

import DeliveryMap from "..";
import styles from "./index.module.scss";

export default function EdostMap({ isError }: any) {
  const dispatch = useDispatch();
  const [points, setPoints] = useState<any[]>([]);
  const { cityName, deliveryCityData, deliveryCityDataLoaded } = useSelector(
    (state: any) => state.location,
  );
  const [isLoading, setIsLoading] = useState(true);
  const { basket, deliveryList, isParamsLoaded, params } = useSelector(
    (state: any) => state.cart,
  );

  const getEdostPoints = async () => {
    setIsLoading(true);
    const deliveryData = await cacheFunctionData(
      () => {
        return Api.edost.getPoints(
          cityName,
          `${deliveryCityData?.region} ${deliveryCityData?.region_type_full}`,
          deliveryCityData?.kladr_id,
        );
      },
      `edostCalc${cityName}`,
      true,
    );

    const edostItem = deliveryList.find((item: any) => {
      return item.TYPE === DeliveryTypes.edost;
    });
    let host = "";
    if (typeof window !== "undefined") {
      // eslint-disable-next-line prefer-destructuring
      host = window.location.host.split(".")[0];
    }
    const edostDiscountPrice = edostItem.DISCOUNT?.[host] || 0;
    let minPriceVal = -1;
    deliveryData.POINTS = deliveryData.POINTS.map((point: any) => {
      if (minPriceVal === -1 || point.PRICE_VAL < minPriceVal) {
        minPriceVal = point.PRICE_VAL;
      }
      return {
        ...point,
        PRICE_VAL: point.PRICE_VAL - edostDiscountPrice,
        PRICE_NO_DISCOUNT: point.PRICE_VAL,
      };
    });
    setPoints(deliveryData.POINTS);
    if (deliveryData.POINTS.length === 0) {
      await dispatch(
        setParam({
          ...params,
          [UserFields.pointId]: 1,
          [UserFields.pointInfo]: "не найден пункт выдачи",
        }),
      );
    }
    dispatch(
      setDeliveryListAdditional(
        Object.values(deliveryData.DELIVERY_LIST).map((item: any) => {
          let { PRICE } = item;
          const { DESCRIPTION } = item;
          const ID = parseInt(item.ID, 10);
          let IS_PRICE_FROM = false;
          // eslint-disable-next-line eqeqeq
          if (ID == 4) {
            IS_PRICE_FROM = true;
            PRICE = minPriceVal;
          }
          return {
            ID,
            NAME: item.NAME,
            DESCRIPTION,
            PARENT_ID: "",
            IS_PRICE_FROM,
            PRICE,
            TYPE: item.TYPE,
            LOCATION: "",
            PRICE_RESTRICTIONS: null,
          };
        }),
      ),
    );
    setIsLoading(false);
  };

  const isAllProductsAvailableForDelivery = useMemo(() => {
    return (
      basket?.ITEMS?.filter((item: any) => {
        return (
          item.WEIGHT > 0 &&
          item.DIMENSIONS?.HEIGHT > 0 &&
          item.DIMENSIONS?.LENGTH > 0 &&
          item.DIMENSIONS?.WIDTH > 0
        );
        // eslint-disable-next-line eqeqeq
      }).length == basket?.ITEMS?.length
    );
  }, [basket]);

  const isBasketItemsExist = useMemo(() => {
    return basket?.ITEMS?.length > 0;
  }, [basket]);

  useEffect(() => {
    if (
      isParamsLoaded &&
      cityName &&
      deliveryCityDataLoaded &&
      isBasketItemsExist &&
      isAllProductsAvailableForDelivery
    ) {
      getEdostPoints();
    }
  }, [
    isParamsLoaded,
    cityName,
    deliveryCityDataLoaded,
    isBasketItemsExist,
    isAllProductsAvailableForDelivery,
  ]);

  const pointsFormatted = useMemo(() => {
    return points
      ? points
          .map((item) => {
            let dopPrice = item.PRICE_VAL + 250;
            if (!["СДЭК", "Деловые линии"].includes(item.TITLE)) {
              dopPrice = null;
            }
            return {
              ID: item.ID,
              NAME: item.ADR,
              TITLE: item.TITLE,
              WORK_TIME: item.WORK_TIME,
              PRICE: item.PRICE_VAL,
              PRICE_DOP: dopPrice,
              IS_TK: true,
              GPS_N: item.GPS_N,
              GPS_S: item.GPS_S,
            };
          })
          .sort((a, b) => a.PRICE - b.PRICE)
      : [];
  }, [points]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {pointsFormatted.length === 0 && !isLoading ? (
        <div>
          <h2>Пункт выдачи</h2>
          <p className={styles.Warning}>
            Не удалось найти пункты выдачи в вашем городе. Попробуйте выбрать
            другой метод доставки или оформить заказ без выбранного пукнта.
            После оформления заказа менеджер свяжется и предложит оптимальный
            способ доставки.
          </p>
        </div>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {!isAllProductsAvailableForDelivery ? (
            <div>
              <h2>Пункт выдачи</h2>
              <p className={styles.Warning}>
                В корзине присутствуют товары которые не доступны для доставки
                транспортной компанией
              </p>
            </div>
          ) : (
            <DeliveryMap
              isError={isError}
              title={
                <div className={styles.Location}>
                  Доставка в {cityName}
                  <Button
                    type="outline"
                    onClick={() => dispatch(showPopup(popupTypes.locationNew))}
                    label="Сменить город?"
                  />
                </div>
              }
              points={pointsFormatted}
              isTk
              isLoading={isLoading}
            />
          )}
        </>
      )}
    </>
  );
}
